// Libs
import { useMutation, type UseMutationResult, useQueryClient } from "@tanstack/react-query";
import { AXIOS_POST_TIMEOUT_MS } from "src/config/axiosTimeout";
import axios from "src/utils/axios";

// Utils
import useAuth from "src/hooks/useAuth";
import { extendMarketingActionWithMainScenario } from "src/utils/formatters";

// Types
import type { SponsoredAdSlot } from "src/types/sponsored-ads";

import type { AxiosResponse } from "axios";
import { isBypassedOrganization } from "src/config/marketingActionBypass";
import { Mutation } from "src/types/mutation";
import type { ConcurrentAction, ReactivationStrategyEnum } from "src/types/offer/ConcurrentAction";
import { OfferStatus } from "src/types/offerSlot";
import { Query } from "src/types/query";
import type { SponsoredAdFormOutput } from "src/views/marketing-actions/marketing-actions-create-view/types/MarketingActionFormOutput";

export interface UseMutationUpdateSponsoredSlotAdByIdParams {
    sponsoredAdSlotId: number;
    action: "suspend" | "delete" | "reactivate";
}

export function useMutationUpdateSponsoredSlotAdById(): UseMutationResult<
    SponsoredAdSlot,
    unknown,
    UseMutationUpdateSponsoredSlotAdByIdParams
> {
    const { user } = useAuth();
    const clientQuery = useQueryClient();

    return useMutation({
        mutationKey: [Mutation.UpdateOneSponsoredAdSlotById, user.id],

        mutationFn: ({ sponsoredAdSlotId, action }: UseMutationUpdateSponsoredSlotAdByIdParams) =>
            axios
                .patch<SponsoredAdSlot>(`/sponsored-ad-slot/${sponsoredAdSlotId}`, {
                    action,
                })
                .then((res) => res.data),

        onSuccess: () => {
            clientQuery.invalidateQueries({
                queryKey: [Query.SponsoredAdSlotsAgenda],
            });
            clientQuery.invalidateQueries({
                queryKey: [Query.SponsoredAdSlotsAgendaNumber],
            });
        },
    });
}

export interface UseMutationUpdateSponsoredAdByIdParams {
    sponsoredAdId: number;
    action: "cancel" | "delete";
    posIds?: number[];
    reactivationStrategy?: ReactivationStrategyEnum;
}

export function useMutationUpdateSponsoredAdById(): UseMutationResult<
    { pass: boolean; overrides?: ConcurrentAction[] },
    unknown,
    UseMutationUpdateSponsoredAdByIdParams
> {
    const { user } = useAuth();
    const clientQuery = useQueryClient();

    return useMutation({
        mutationKey: [Mutation.UpdateOneSponsoredAdById, user.id],

        mutationFn: async ({
            sponsoredAdId,
            action,
            posIds,
            reactivationStrategy,
        }: UseMutationUpdateSponsoredAdByIdParams) => {
            const response = await axios.patch<{ pass: boolean; overrides?: ConcurrentAction[] }>(
                `/sponsored-ad/${sponsoredAdId}`,
                {
                    action,
                    pos: posIds,
                    reactivationStrategy,
                }
            );

            return {
                ...response.data,
                overrides: response.data.overrides && [
                    ...response.data.overrides.map((ove) => {
                        return {
                            ...ove,
                            marketingAction: extendMarketingActionWithMainScenario(
                                ove.marketingAction
                            ),
                        };
                    }),
                ],
            };
        },

        onSuccess: () => {
            clientQuery.invalidateQueries({
                queryKey: [Query.SponsoredAdManagement],
            });
        },
    });
}

export async function createSponsoredAd(
    sponsoredAd: SponsoredAdFormOutput,
    organizationId?: number
): Promise<AxiosResponse<any>> {
    try {
        return await axios.post(`/sponsored-ad`, sponsoredAd, { timeout: AXIOS_POST_TIMEOUT_MS });
    } catch (e: any) {
        if (e?.code === "ECONNABORTED" && isBypassedOrganization(organizationId)) {
            return { data: { offer: { status: OfferStatus.InCreation } } } as AxiosResponse<any>;
        }
        return;
    }
}
